import React, { useEffect, useState } from 'react';
import { Box, FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { AppDispatch } from '../../stateManagement/store';
import { useDispatch } from 'react-redux';
import { IOrganizationTaskRequest } from "@ontime/types";
import { selectSelectedOrganization } from '../../stateManagement/selectedOrganizationSlice';
import { useSelector } from 'react-redux';
import { createOrganizationTaskAsync, updateOrganizationTaskAsync } from '../../stateManagement/organizationTaskSlice';
import CustomDialogTitle from '../../customComponents/CustomDialogTitle';
import CustomDialogContent from '../../customComponents/CustomDialogContent';
import CustomFormAction from '../../customComponents/CustomFormAction';
import CustomButton from '../../customComponents/CustomButton';
import InfoComponent from '../../customComponents/InfoComponent';

export const organizationTaskSchema = z.object({
    organizationTaskId: z.string(),
    title: z.string().min(1, "Title cannot be empty.").max(100, "Maximum length of Title is 100 characters."),
    price: z.number().min(0, "Price cannot be under 0.").max(100000000000000, "Maximum length of Title is 100000000000000 characters."),
    duration: z.number().min(1, "Duration cannot be 0.").max(1000, "Maximum length of Duration is 1000 minutes."),
    description: z.string().max(500, "Maximum length of Address is 500 characters.").optional(),
    isPublic: z.boolean(),
});

interface OrganizationTaskFormProps {
    organizationTaskInput?: IOrganizationTaskRequest | null;
    closeForm: () => void;
}

const OrganizationTaskForm: React.FC<OrganizationTaskFormProps> = ({ organizationTaskInput, closeForm }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch<AppDispatch>();

    const organization = useSelector(selectSelectedOrganization);

    const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<IOrganizationTaskRequest>({
        resolver: zodResolver(organizationTaskSchema),
        defaultValues: {
            organizationTaskId: '',
            title: '',
            isPublic: true,
            price: 0,
            duration: 15,
        }
    });

    useEffect(() => {
        if (organizationTaskInput?.organizationTaskId) {
            reset({
                organizationTaskId: organizationTaskInput.organizationTaskId,
                title: organizationTaskInput.title ?? '',
                price: organizationTaskInput.price ?? 0,
                duration: organizationTaskInput.duration ?? 0,
                description: organizationTaskInput.description ?? '',
                isPublic: organizationTaskInput.isPublic as boolean,
            });
        }
        else {
            reset({
                organizationTaskId: '',
                title: '',
                isPublic: true,
                price: 0,
                duration: 15,
            });
        }

        setLoading(false);


    }, [organizationTaskInput, reset]);

    const handleSave = async (data: IOrganizationTaskRequest) => {

        setLoading(true);
        try {
            let result;
            if (organizationTaskInput?.organizationTaskId) {
                result = await dispatch(updateOrganizationTaskAsync({ organizationId: organization.organizationId ?? '', data })).unwrap();
            } else {
                result = await dispatch(createOrganizationTaskAsync({ organizationId: organization.organizationId ?? '', data })).unwrap();
            }
            if (result) {
                closeForm();
            }
        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <CustomDialogTitle handleClose={closeForm} title={organizationTaskInput?.organizationTaskId ? 'Edit Service' : 'New Service'} loading={loading} />
            <CustomDialogContent>
                <form onSubmit={handleSubmit(handleSave)} noValidate>


                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Title"
                            InputLabelProps={{ shrink: true }}
                            {...register('title')}
                            error={!!errors.title}
                            helperText={errors.title?.message}
                        />
                    </Box>

                    <Box mt={2}>
                        <InfoComponent infoContent='Specify how long this service takes in minutes. This duration will be used to schedule appointments.'>

                            <TextField
                                variant="outlined"
                                fullWidth
                                type="number"
                                label="Duration (minutes)"
                                InputLabelProps={{ shrink: true }}
                                {...register('duration', { valueAsNumber: true })}
                                error={!!errors.duration}
                                helperText={errors.duration?.message}
                                inputProps={{ step: "1", min: 0 }}
                            />
                        </InfoComponent>
                    </Box>

                    <Box mt={2}>
                        <InfoComponent infoContent='Enter the price for this service. This amount will be used during the payment process.'>
                            <TextField
                                fullWidth
                                type="number"
                                label="Price"
                                InputLabelProps={{ shrink: true }}
                                {...register('price', { valueAsNumber: true })}
                                error={!!errors.price}
                                helperText={errors.price?.message}
                                inputProps={{ step: "0.01", min: 0 }}
                            />
                        </InfoComponent>
                    </Box>

                    <Box mt={2}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            label="Description"
                            InputLabelProps={{ shrink: true }}
                            {...register('description')}
                            error={!!errors.description}
                            helperText={errors.description?.message}
                            multiline
                        />
                    </Box>

                    <Box mt={2}>
                        <FormControl fullWidth error={!!errors.isPublic}>
                            <InputLabel>Accessible</InputLabel>
                            <Select
                                label="Accessible"
                                value={watch('isPublic') ?? true} // fallback to `true` instead of ''
                                onChange={(e) => setValue('isPublic', e.target.value === 'true')}
                            >
                                <MenuItem value="true">Public</MenuItem>
                                <MenuItem value="false">Private</MenuItem>
                            </Select>
                            <FormHelperText>{errors.isPublic?.message}</FormHelperText>
                        </FormControl>
                    </Box>

                    {watch('price') === 0 && <FormHelperText error sx={{ ml: 2 }}>Please enter a price for the service to enable payment processing.</FormHelperText>}


                    <CustomFormAction>
                        <CustomButton lable='Save' type='submit' disabled={loading} infoContent={`Save the ${organizationTaskInput?.organizationTaskId ? 'updated' : 'new'} service record`} />
                    </CustomFormAction>

                </form>
            </CustomDialogContent>
        </Box>
    );
};

export default OrganizationTaskForm;